import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/components/feature/Toast";
import StarRating, { StarRatingInput } from "@/components/feature/StarRating";
import {
  deleteReview,
  fetchProductReviews,
  submitReview,
  summarizeRatings,
  type ProductReview,
} from "@/lib/reviews";

interface ProductReviewsProps {
  productId: number;
  sellerId: string;
}

function reviewDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/**
 * Sección de valoraciones del producto: muestra la reputación del vendedor,
 * las reseñas recibidas y permite al comprador calificar (una vez por producto).
 */
export default function ProductReviews({
  productId,
  sellerId,
}: ProductReviewsProps) {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);

  const isSeller = Boolean(user && user.id === sellerId);
  const myReview = reviews.find((r) => r.reviewer_id === user?.id) ?? null;
  const summary = summarizeRatings(reviews);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProductReviews(productId);
      setReviews(data);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "No se pudieron cargar las valoraciones.",
      );
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    load();
  }, [load]);

  // Precarga la reseña existente del usuario en el formulario.
  useEffect(() => {
    if (ready) return;
    if (!user) {
      setReady(true);
      return;
    }
    if (!loading) {
      const mine = reviews.find((r) => r.reviewer_id === user.id);
      if (mine) {
        setRating(mine.rating);
        setComment(mine.comment ?? "");
      }
      setReady(true);
    }
  }, [loading, reviews, user, ready]);

  async function handleSubmit() {
    if (!user || saving) return;
    setSaving(true);
    try {
      await submitReview({
        productId,
        sellerId,
        reviewerId: user.id,
        rating,
        comment,
      });
      showToast(
        myReview ? "Valoración actualizada" : "¡Gracias por tu valoración!",
        "success",
      );
      await load();
    } catch (e) {
      showToast(
        e instanceof Error ? e.message : "No se pudo guardar la valoración.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!user || saving) return;
    setSaving(true);
    try {
      await deleteReview(productId, user.id);
      setRating(5);
      setComment("");
      showToast("Valoración eliminada", "success");
      await load();
    } catch (e) {
      showToast(
        e instanceof Error ? e.message : "No se pudo eliminar la valoración.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="mt-8 border-t border-background-200/70 pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-base font-semibold text-foreground-950">
          Valoraciones
        </h2>
        {summary.count > 0 ? (
          <StarRating
            value={summary.average}
            count={summary.count}
            size="md"
            showValue
          />
        ) : (
          <span className="text-sm text-foreground-500">
            Todavía sin valoraciones
          </span>
        )}
      </div>

      {/* Formulario para calificar */}
      <div className="mt-4 rounded-lg border border-background-200/70 bg-background-100/60 p-4">
        {!user ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              Iniciá sesión para valorar a este vendedor.
            </p>
            <Link
              to="/login"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              <i className="ri-login-box-line"></i>
              Iniciar sesión
            </Link>
          </div>
        ) : isSeller ? (
          <p className="flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-information-line text-base text-foreground-400"></i>
            No podés valorar tu propia publicación.
          </p>
        ) : (
          <div>
            <p className="text-sm font-semibold text-foreground-900">
              {myReview ? "Tu valoración" : "¿Cómo fue tu experiencia?"}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <StarRatingInput
                value={rating}
                onChange={setRating}
                disabled={saving}
              />
              <span className="text-sm text-foreground-600">
                {["Muy malo", "Malo", "Regular", "Bueno", "Excelente"][rating - 1]}
              </span>
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value.slice(0, 500))}
              rows={3}
              maxLength={500}
              placeholder="Contá cómo fue tu experiencia con este vendedor (opcional)."
              className="mt-3 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
              <span className="text-xs text-foreground-400">
                {comment.length}/500
              </span>
              <div className="flex items-center gap-2">
                {myReview && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={saving}
                    className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Eliminar
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : (
                    <i className="ri-send-plane-fill"></i>
                  )}
                  {myReview ? "Actualizar valoración" : "Publicar valoración"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Lista de reseñas */}
      <div className="mt-5">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <p className="text-sm text-foreground-500">{error}</p>
            <button
              type="button"
              onClick={load}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-1.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
            >
              Reintentar
            </button>
          </div>
        ) : reviews.length === 0 ? (
          <p className="py-4 text-center text-sm text-foreground-500">
            Sé el primero en valorar este producto.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-background-200/70">
            {reviews.map((review) => (
              <li key={review.id} className="flex gap-3 py-4">
                {review.reviewer_avatar ? (
                  <img
                    src={review.reviewer_avatar}
                    alt={review.reviewer_name}
                    className="h-9 w-9 shrink-0 rounded-full border border-background-200 object-cover object-top"
                  />
                ) : (
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-sm font-semibold text-white">
                    {review.reviewer_name.charAt(0).toUpperCase()}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm font-semibold text-foreground-950">
                      {review.reviewer_name}
                    </span>
                    <StarRating value={review.rating} size="sm" />
                    <span className="text-xs text-foreground-400">
                      {reviewDate(review.created_at)}
                    </span>
                  </div>
                  {review.comment && (
                    <p className="mt-1 whitespace-pre-line break-words text-sm leading-relaxed text-foreground-600">
                      {review.comment}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}